import React, { useState } from 'react';
import { useProfile } from '../hooks/useProfile';
import { usePasswordChange } from '../hooks/usePasswordChange';
import ProfileHeader from '../components/ProfileHeader';
import ProfileForm from '../components/ProfileForm';
import PasswordChangeForm from '../components/PasswordChangeForm';
import AvatarUpload from '../components/AvatarUpload';

const ProfilePage = () => {
  const {
    profile,
    loading: profileLoading,
    error: profileError,
    success: profileSuccess,
    updateProfile,
    uploadAvatar,
    removeAvatar,
    clearMessages: clearProfileMessages,
  } = useProfile();

  const {
    changePassword,
    loading: passwordLoading,
    error: passwordError,
    success: passwordSuccess,
    clearMessages: clearPasswordMessages,
  } = usePasswordChange();

  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', name: 'Profile Information' },
    { id: 'password', name: 'Change Password' },
  ];

  const handleUpdateProfile = async (data) => {
    try {
      await updateProfile(data);
    } catch (error) {
      // Error is handled by hook
    }
  };

  const handlePasswordChange = async (data) => {
    await changePassword(data);
  };

  const handleAvatarUpload = async (file) => {
    try {
      await uploadAvatar(file);
    } catch (error) {
      // Error is handled by hook
    }
  };

  const handleAvatarRemove = async () => {
    try {
      await removeAvatar();
    } catch (error) {
      // Error is handled by hook
    }
  };

  if (profileLoading && !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600">Manage your personal information and security settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column - Avatar */}
        <div className="lg:col-span-1">
          <AvatarUpload
            avatar={profile?.avatar}
            onUpload={handleAvatarUpload}
            onRemove={handleAvatarRemove}
            uploading={profileLoading}
          />
        </div>

        {/* Right column - Profile details */}
        <div className="lg:col-span-2 space-y-6">
          <ProfileHeader profile={profile} />

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    clearProfileMessages();
                    clearPasswordMessages();
                  }}
                  className={`
                    py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div>
            {activeTab === 'profile' && (
              <ProfileForm
                profile={profile}
                onSubmit={handleUpdateProfile}
                loading={profileLoading}
                error={profileError}
                success={profileSuccess}
              />
            )}

            {activeTab === 'password' && (
              <PasswordChangeForm
                onSubmit={handlePasswordChange}
                loading={passwordLoading}
                error={passwordError}
                success={passwordSuccess}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;